import type { EventDetails, RegistrationStatus } from "@/modules/events/types/eventDetailsApi"

import { addMinutes, isAfter, isBefore } from "date-fns"

import { SLOT_REGISTRATION_STATUS } from "@/modules/events/constants"
import { getUpdatedSubeventsWithStatus } from "@/modules/events/utils/subevents"

type getConferenceUrlToDisplayParams = {
  registrationStatus: RegistrationStatus
  event: EventDetails
}

export type getIsLinkDisplayedParams = {
  startDate: string | Date
  conferenceUrl: string
}

// Determines which link conference url should be displayed at the moment (main event or subevent)
// If you are subscribed to a subevent that is happening now or in less than 5 minutes and it has a url, it returns its url
// Otherwise, it returns the main event url (even if empty)
export const getConferenceUrlToDisplay = ({ registrationStatus, event }: getConferenceUrlToDisplayParams): string => {
  const subeventsWithStatus = getUpdatedSubeventsWithStatus({
    subeventStatuses: registrationStatus.slots || [],
    subevents: event.subevents || [],
  })

  const subscribedSubeventsWithUrlNowOrSoon = subeventsWithStatus
    // only subscribed subevents
    .filter(subevent => subevent.status === SLOT_REGISTRATION_STATUS.SLOT_REGISTRATION_STATUS_SUBSCRIBED)
    // only subevents with a conference url
    .filter(subevent => subevent.videoconferenceUrl.length > 0)
    // only subevents that are happening now or in less than 5 minutes
    .filter(subevent => {
      const startDate = new Date(subevent.startAt)
      const displayLinkTime = addMinutes(new Date(startDate), -5)
      const endDate = new Date(subevent.endAt)
      const now = new Date()
      return isAfter(now, displayLinkTime) && isBefore(now, endDate)
    })
    // sort by start date
    .sort((subeventA, subeventB) => {
      const startDateA = new Date(subeventA.startAt)
      const startDateB = new Date(subeventB.startAt)
      return startDateA.getTime() - startDateB.getTime()
    })

  const mainEventConferenceUrl = registrationStatus.conferenceUrl

  // if there are multiple subevents the user is registered to happening now or soon, we display the most recent one
  // if there is one subevent happening now or soon, we display it
  // if there are no subevents happening now or soo , we display the main event url
  const urlToDisplay =
    subscribedSubeventsWithUrlNowOrSoon.length > 0
      ? subscribedSubeventsWithUrlNowOrSoon[subscribedSubeventsWithUrlNowOrSoon.length - 1].videoconferenceUrl
      : mainEventConferenceUrl

  return urlToDisplay || ""
}

// Link is displayed 5 minutes before the event starts, if there is a conference url
export const getIsLinkDisplayed = ({ startDate, conferenceUrl }: getIsLinkDisplayedParams): boolean => {
  const displayLinkTime = addMinutes(new Date(startDate), -5)
  const currentDate = new Date()
  const conferenceUrlExists = typeof conferenceUrl !== "undefined" && conferenceUrl.length > 0

  return !isBefore(currentDate, displayLinkTime) && conferenceUrlExists
}
